import React, { useState } from 'react';
import { 
  Trash2, DollarSign, FolderOpen, Eye, EyeOff, 
  Package, Truck, FileText, X, CheckSquare, Square
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const BulkActionsToolbar = ({ 
  selectedCount, 
  selectedIds, 
  categories = [],
  onClearSelection,
  onSelectAll,
  totalCount,
  allSelected,
  onRefresh
}) => {
  const [activeDialog, setActiveDialog] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Price change state
  const [priceChangeType, setPriceChangeType] = useState('percentage_add');
  const [priceValue, setPriceValue] = useState('');
  
  // Category change state
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Stock change state
  const [stockAction, setStockAction] = useState('set');
  const [stockValue, setStockValue] = useState('');
  
  // Shipping cost state
  const [shippingCost, setShippingCost] = useState('');
  
  // Description state
  const [descAction, setDescAction] = useState('append');
  const [descText, setDescText] = useState('');

  const closeDialog = () => {
    setActiveDialog(null);
    setPriceValue('');
    setSelectedCategory('');
    setStockValue('');
    setShippingCost('');
    setDescText('');
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Möchten Sie wirklich ${selectedCount} Produkt(e) unwiderruflich löschen?`)) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.post('/bulk/delete', {
        product_ids: selectedIds
      });
      toast.success(response.data.message);
      onClearSelection();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = async () => {
    if (!priceValue || isNaN(parseFloat(priceValue))) {
      toast.error('Bitte geben Sie einen gültigen Wert ein');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.post('/bulk/price', {
        product_ids: selectedIds,
        change_type: priceChangeType,
        value: parseFloat(priceValue)
      });
      toast.success(response.data.message);
      closeDialog();
      onClearSelection();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Preisänderung');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = async () => {
    if (!selectedCategory) {
      toast.error('Bitte wählen Sie eine Kategorie');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.post('/bulk/category', {
        product_ids: selectedIds,
        category: selectedCategory
      });
      toast.success(response.data.message);
      closeDialog();
      onClearSelection();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Verschieben');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (isActive) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/bulk/status', {
        product_ids: selectedIds,
        is_active: isActive
      });
      toast.success(response.data.message);
      onClearSelection();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Statusänderung');
    } finally {
      setLoading(false);
    }
  };

  const handleStockChange = async () => {
    if (stockValue === '' || isNaN(parseInt(stockValue))) {
      toast.error('Bitte geben Sie einen gültigen Wert ein');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.post('/bulk/stock', {
        product_ids: selectedIds,
        stock_action: stockAction,
        value: parseInt(stockValue)
      });
      toast.success(response.data.message);
      closeDialog();
      onClearSelection();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Bestandsänderung');
    } finally {
      setLoading(false);
    }
  };

  const handleShippingChange = async () => {
    if (shippingCost === '' || isNaN(parseFloat(shippingCost))) {
      toast.error('Bitte geben Sie einen gültigen Wert ein');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.post('/bulk/shipping', {
        product_ids: selectedIds,
        shipping_cost: parseFloat(shippingCost)
      });
      toast.success(response.data.message);
      closeDialog();
      onClearSelection();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Versandkostenänderung');
    } finally {
      setLoading(false);
    }
  };

  const handleDescriptionChange = async () => {
    if (!descText.trim()) {
      toast.error('Bitte geben Sie einen Text ein');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axiosInstance.post('/bulk/description', {
        product_ids: selectedIds,
        action: descAction,
        text: descText.trim()
      });
      toast.success(response.data.message);
      closeDialog();
      onClearSelection();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Beschreibungsänderung');
    } finally {
      setLoading(false);
    }
  };

  if (selectedCount === 0) return null;

  return (
    <>
      {/* Floating Toolbar */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 flex items-center gap-2 flex-wrap max-w-[95vw]">
        {/* Selection Info */}
        <div className="flex items-center gap-2 pr-3 border-r border-gray-200">
          <button
            onClick={onSelectAll}
            className="p-1.5 hover:bg-gray-100 rounded"
            title={allSelected ? "Auswahl aufheben" : "Alle auswählen"}
          >
            {allSelected ? (
              <CheckSquare className="h-5 w-5 text-blue-600" />
            ) : (
              <Square className="h-5 w-5 text-gray-400" />
            )}
          </button>
          <span className="text-sm font-medium text-gray-700">
            {selectedCount} von {totalCount} ausgewählt
          </span>
          <button
            onClick={onClearSelection}
            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
            title="Auswahl aufheben"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveDialog('price')}
            className="text-gray-700"
            disabled={loading}
          >
            <DollarSign className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Preis</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveDialog('category')}
            className="text-gray-700"
            disabled={loading}
          >
            <FolderOpen className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Kategorie</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleStatusChange(true)}
            className="text-green-600"
            disabled={loading}
          >
            <Eye className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Aktivieren</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleStatusChange(false)}
            className="text-orange-600"
            disabled={loading}
          >
            <EyeOff className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Deaktivieren</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveDialog('stock')}
            className="text-gray-700"
            disabled={loading}
          >
            <Package className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Bestand</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveDialog('shipping')}
            className="text-gray-700"
            disabled={loading}
          >
            <Truck className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Versand</span>
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveDialog('description')}
            className="text-gray-700"
            disabled={loading}
          >
            <FileText className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Beschreibung</span>
          </Button>
          
          <div className="w-px h-6 bg-gray-200 mx-1" />
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBulkDelete}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            disabled={loading}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Löschen</span>
          </Button>
        </div>
      </div>

      {/* Price Change Dialog */}
      <Dialog open={activeDialog === 'price'} onOpenChange={() => closeDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Preis ändern ({selectedCount} Produkte)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Art der Änderung</Label>
              <Select value={priceChangeType} onValueChange={setPriceChangeType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="set">Neuen Preis festlegen</SelectItem>
                  <SelectItem value="percentage_add">Prozent aufschlagen (+%)</SelectItem>
                  <SelectItem value="percentage_subtract">Prozent reduzieren (-%)</SelectItem>
                  <SelectItem value="fixed">Festen Betrag addieren/subtrahieren (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>
                {priceChangeType === 'set' && 'Neuer Preis (€)'}
                {priceChangeType === 'percentage_add' && 'Aufschlag (%)'}
                {priceChangeType === 'percentage_subtract' && 'Reduzierung (%)'}
                {priceChangeType === 'fixed' && 'Betrag (€, negativ für Abzug)'}
              </Label>
              <Input
                type="number"
                step={priceChangeType.includes('percentage') ? '1' : '0.01'}
                value={priceValue}
                onChange={(e) => setPriceValue(e.target.value)}
                placeholder={priceChangeType.includes('percentage') ? 'z.B. 10' : 'z.B. 5.00'}
                className="mt-1"
              />
              {priceChangeType === 'percentage_add' && priceValue && (
                <p className="text-xs text-gray-500 mt-1">
                  Beispiel: 100€ → {formatEUR((100 * (1 + parseFloat(priceValue || 0) / 100)))}
                </p>
              )}
              {priceChangeType === 'percentage_subtract' && priceValue && (
                <p className="text-xs text-gray-500 mt-1">
                  Beispiel: 100€ → {formatEUR((100 * (1 - parseFloat(priceValue || 0) / 100)))}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Abbrechen</Button>
            <Button onClick={handlePriceChange} disabled={loading}>
              {loading ? 'Wird geändert...' : 'Preis ändern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Change Dialog */}
      <Dialog open={activeDialog === 'category'} onOpenChange={() => closeDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Kategorie ändern ({selectedCount} Produkte)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Neue Kategorie</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Kategorie wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.name || cat} value={cat.name || cat}>
                      {cat.name || cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Abbrechen</Button>
            <Button onClick={handleCategoryChange} disabled={loading || !selectedCategory}>
              {loading ? 'Wird verschoben...' : 'Verschieben'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Change Dialog */}
      <Dialog open={activeDialog === 'stock'} onOpenChange={() => closeDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lagerbestand ändern ({selectedCount} Produkte)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Aktion</Label>
              <Select value={stockAction} onValueChange={setStockAction}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="set">Auf Wert setzen</SelectItem>
                  <SelectItem value="add">Hinzufügen</SelectItem>
                  <SelectItem value="subtract">Abziehen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Menge</Label>
              <Input
                type="number"
                min="0"
                value={stockValue}
                onChange={(e) => setStockValue(e.target.value)}
                placeholder="z.B. 10"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Abbrechen</Button>
            <Button onClick={handleStockChange} disabled={loading}>
              {loading ? 'Wird geändert...' : 'Bestand ändern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shipping Cost Dialog */}
      <Dialog open={activeDialog === 'shipping'} onOpenChange={() => closeDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Versandkosten ändern ({selectedCount} Produkte)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Neue Versandkosten (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={shippingCost}
                onChange={(e) => setShippingCost(e.target.value)}
                placeholder="z.B. 3.49"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                0 = Kostenloser Versand
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Abbrechen</Button>
            <Button onClick={handleShippingChange} disabled={loading}>
              {loading ? 'Wird geändert...' : 'Versandkosten setzen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Description Change Dialog */}
      <Dialog open={activeDialog === 'description'} onOpenChange={() => closeDialog()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Beschreibung ändern ({selectedCount} Produkte)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Aktion</Label>
              <Select value={descAction} onValueChange={setDescAction}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="append">Text am Ende hinzufügen</SelectItem>
                  <SelectItem value="prepend">Text am Anfang hinzufügen</SelectItem>
                  <SelectItem value="replace">Beschreibung ersetzen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Text</Label>
              <Textarea
                value={descText}
                onChange={(e) => setDescText(e.target.value)}
                placeholder={descAction === 'replace' 
                  ? 'Neue Beschreibung eingeben...' 
                  : 'Text der hinzugefügt werden soll...'}
                className="mt-1 min-h-[120px]"
              />
              {descAction === 'replace' && (
                <p className="text-xs text-orange-600 mt-1">
                  Achtung: Die bestehende Beschreibung wird komplett ersetzt!
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Abbrechen</Button>
            <Button onClick={handleDescriptionChange} disabled={loading || !descText.trim()}>
              {loading ? 'Wird geändert...' : 'Beschreibung ändern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkActionsToolbar;
